import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { formatDate } from '@/lib/format';
import {
  TechnicianLicensesNotPermittedError,
  useDeleteTechnicianLicense,
  useTechnicianLicenses,
  useUploadLicenseDocument,
  type TechnicianLicense,
} from '@/routes/technicians/useTechnicianLicenses';
import { LicenseRowExpiryChip } from './LicenseExpiryBadge';
import { TechnicianLicenseDialog } from './TechnicianLicenseDialog';

/**
 * A technician's licences on their profile page (M1-37).
 *
 * <b>Read-only unless the viewer holds `users.manage`</b> — and that includes
 * the technician looking at their OWN licences. M1-24 gave them a self-edit
 * seam for their emergency contact; the AC deliberately leaves licences out of
 * that field set, so there is no "edit mine" affordance here at all rather
 * than one that renders and then 403s. A credential you can award yourself is
 * not evidence of anything.
 *
 * The API is still the enforcement point (web/CONVENTIONS.md); this only
 * decides what to draw.
 */
export function TechnicianLicenseSection({
  profileId,
  technicianName,
  canManage,
  onAnnounce,
}: {
  profileId: string;
  technicianName: string;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const licenses = useTechnicianLicenses(profileId);
  const remove = useDeleteTechnicianLicense(profileId);

  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; license: TechnicianLicense } | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState<TechnicianLicense | null>(null);

  // A technician viewing someone else's profile: the API refuses, and this is
  // a real state to render rather than a load failure to retry.
  if (licenses.error instanceof TechnicianLicensesNotPermittedError) {
    return null;
  }

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('technicianLicenses.heading')}</h2>
        {canManage ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setDialog({ mode: 'create' })}>
            {t('technicianLicenses.addAction')}
          </Button>
        ) : null}
      </div>

      {licenses.isPending ? (
        <div className="mt-2">
          <LoadingState label={t('technicianLicenses.loading')} />
        </div>
      ) : licenses.isError ? (
        <div className="mt-2">
          <ErrorState
            title={t('technicianLicenses.loadErrorTitle')}
            description={t('technicianLicenses.loadErrorDescription')}
            onRetry={() => void licenses.refetch()}
          />
        </div>
      ) : licenses.data.length === 0 ? (
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          {canManage ? t('technicianLicenses.emptyCanManage') : t('technicianLicenses.emptyReadOnly')}
        </p>
      ) : (
        <ul className="mt-3 grid gap-3">
          {licenses.data.map((license) => (
            <LicenseRow
              key={license.id}
              profileId={profileId}
              license={license}
              canManage={canManage}
              onEdit={() => setDialog({ mode: 'edit', license })}
              // reset() first: this mutation is hoisted to the section and
              // outlives each ConfirmDialog, unlike the sibling archive/delete
              // dialogs that own theirs and get a fresh one per open. Without
              // it a failed delete leaves isError true, and the NEXT licence's
              // dialog opens already announcing that failure via role="alert"
              // — an error about an action this user has not taken yet.
              onDelete={() => {
                remove.reset();
                setPendingDelete(license);
              }}
              onAnnounce={onAnnounce}
            />
          ))}
        </ul>
      )}

      {dialog ? (
        <TechnicianLicenseDialog
          profileId={profileId}
          technicianName={technicianName}
          license={dialog.mode === 'edit' ? dialog.license : undefined}
          onClose={() => setDialog(null)}
          onSaved={onAnnounce}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          titleId="delete-license-title"
          title={t('technicianLicenses.delete.title')}
          description={t('technicianLicenses.delete.description', {
            label: describe(pendingDelete, t),
          })}
          confirmLabel={t('technicianLicenses.delete.confirm')}
          isPending={remove.isPending}
          errorMessage={remove.isError ? t('technicianLicenses.delete.failed') : undefined}
          testId="delete-license-dialog"
          onConfirm={() =>
            remove.mutate(pendingDelete.id, {
              onSuccess: () => {
                // The caller's caller does the t() call, never the callback
                // itself — web/CONVENTIONS.md's M1-30 note.
                onAnnounce(t('technicianLicenses.delete.done'));
                setPendingDelete(null);
              },
            })
          }
          onClose={() => setPendingDelete(null)}
        />
      ) : null}
    </div>
  );
}

/** What to call this licence in prose — its class, or the free text an "Other" carries. */
function describe(license: TechnicianLicense, t: (key: string) => string): string {
  return license.class === 'Other' && license.classOtherLabel
    ? license.classOtherLabel
    : t(`technicianLicenses.class.${license.class}`);
}

function LicenseRow({
  profileId,
  license,
  canManage,
  onEdit,
  onDelete,
  onAnnounce,
}: {
  profileId: string;
  license: TechnicianLicense;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const upload = useUploadLicenseDocument(profileId);
  const inputRef = useRef<HTMLInputElement>(null);

  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      upload.mutate(
        { licenseId: license.id, file },
        { onSuccess: () => onAnnounce(t('technicianLicenses.document.uploaded')) },
      );
    }
  }

  return (
    <li className="grid gap-2 rounded-md border p-3" data-testid={`license-${license.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{describe(license, t)}</span>
        <LicenseRowExpiryChip
          expiryState={license.expiryState}
          daysUntilExpiry={license.daysUntilExpiry === null ? null : Number(license.daysUntilExpiry)}
        />
        {/* Administrative status is a separate fact from expiry, so it gets
            its own chip — a revoked licence whose date has not passed is not
            an expiry problem, and folding them together would hide one. Only
            shown when it is NOT the unremarkable Active. */}
        {license.status !== 'Active' ? (
          <span className="inline-block rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
            {t(`technicianLicenses.status.${license.status}`)}
          </span>
        ) : null}
      </div>

      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">
            {t('technicianLicenses.row.jurisdiction')}
          </dt>
          <dd>{license.issuingJurisdiction ?? t('technicianProfile.notSet')}</dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">
            {t('technicianLicenses.row.number')}
          </dt>
          <dd>{license.number ?? t('technicianProfile.notSet')}</dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">
            {t('technicianLicenses.row.expires')}
          </dt>
          <dd>{formatLicenseDate(license.expiresOn) ?? t('technicianLicenses.row.noExpiry')}</dd>
        </div>
      </dl>

      {license.notes ? <p className="text-sm whitespace-pre-line">{license.notes}</p> : null}

      <div className="flex flex-wrap items-center gap-2">
        {license.documentThumbnailUrl ? (
          <img
            src={license.documentThumbnailUrl}
            alt={t('technicianLicenses.document.alt', { label: describe(license, t) })}
            className="h-12 w-12 rounded object-cover"
          />
        ) : null}

        {canManage ? (
          <>
            {/* ⚠️ Hidden from the accessibility tree AND taken out of the tab
                order (M1-47). The note this replaces had already spotted that
                "the adjacent Button is what anyone actually clicks" and then
                answered the wrong question: it silenced axe's label rule and
                left the input FOCUSABLE, because `sr-only` clips an element to
                1×1px without removing it from the tab order. Every licence row
                therefore cost two tab stops, the first of them invisible.

                It takes both attributes — hidden from the tree and still
                focusable is what axe's `aria-hidden-focus` rule exists for.
                ⚠️ Though not a rule this project's scans can demonstrate: see
                the note in `TechnicianPhotoUpload`.

                ⚠️ The `aria-label` goes with them: naming an element nothing
                can reach is dead markup, and keeping it would imply the input
                is still a control somebody navigates to. Its i18n key
                (`technicianLicenses.document.inputLabel`) is removed rather
                than left orphaned. */}
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              data-testid={`license-document-input-${license.id}`}
              aria-hidden="true"
              tabIndex={-1}
              onChange={onFileChosen}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              {upload.isPending
                ? t('technicianLicenses.document.uploading')
                : license.documentStatus === 'Ready'
                  ? t('technicianLicenses.document.replace')
                  : t('technicianLicenses.document.upload')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onEdit}>
              {t('technicianLicenses.editAction')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onDelete}>
              {t('technicianLicenses.deleteAction')}
            </Button>
          </>
        ) : null}
      </div>

      {upload.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('technicianLicenses.document.failed')}
        </p>
      ) : null}
    </li>
  );
}

/**
 * A bare DateOnly string ("2027-03-01"), formatted on the UTC clock — the same
 * trap `hireDate` documents in web/CONVENTIONS.md's M1-24 section. Through
 * `new Date(...)` it anchors at UTC midnight, so the viewer's own zone would
 * print the previous calendar day for anyone west of UTC, and an expiry date
 * that reads a day early is exactly the kind of wrong nobody would question.
 */
function formatLicenseDate(value: string | null): string | null {
  return value ? formatDate(value, { dateStyle: 'medium', timeZone: 'UTC' }) : null;
}
